import React, { useEffect, useState } from 'react';

/**
 * Self-service cache / Service Worker cleaner.
 *
 * Problem: after a Publish, the browser may keep serving the old bundle from
 * the PWA Service Worker + HTTP cache. Normally fixed by opening DevTools and
 * unregistering the SW manually — but that requires right-click / F12, which
 * some users have blocked (extensions, kiosk mode, locked-down browsers).
 *
 * This page does it with one click. Visit /clear-cache (public, no auth).
 */
const ClearCache = () => {
  const [log, setLog] = useState([]);
  const [done, setDone] = useState(false);
  const [running, setRunning] = useState(false);

  const push = (line) => setLog((l) => [...l, line]);

  const run = async () => {
    setRunning(true);
    setLog([]);
    setDone(false);

    try {
      // 1. Unregister every service worker on this origin
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        if (regs.length === 0) {
          push('No service workers were registered.');
        } else {
          for (const r of regs) {
            try {
              await r.unregister();
              push(`Unregistered service worker: ${r.scope}`);
            } catch (e) {
              push(`Could not unregister ${r.scope}: ${e.message}`);
            }
          }
        }
      } else {
        push('Service Worker API not supported in this browser.');
      }

      // 2. Delete every Cache Storage entry (PWA offline caches)
      if ('caches' in window) {
        const keys = await caches.keys();
        if (keys.length === 0) {
          push('No cache storage entries found.');
        } else {
          for (const k of keys) {
            try {
              await caches.delete(k);
              push(`Deleted cache: ${k}`);
            } catch (e) {
              push(`Could not delete cache ${k}: ${e.message}`);
            }
          }
        }
      }

      // 3. Clear localStorage flags that may hold stale state (preserve auth)
      try {
        const authToken = localStorage.getItem('auth_token');
        const authUser = localStorage.getItem('auth_user');
        localStorage.clear();
        if (authToken) localStorage.setItem('auth_token', authToken);
        if (authUser) localStorage.setItem('auth_user', authUser);
        push('Local storage cleared (your login was kept).');
      } catch (e) {
        push(`Local storage clear skipped: ${e.message}`);
      }

      // 4. Clear sessionStorage
      try {
        sessionStorage.clear();
        push('Session storage cleared.');
      } catch { /* noop */ }

      push('All done. Reloading the site with a fresh copy in 2 seconds...');
      setDone(true);

      setTimeout(() => {
        // Hard reload — bypass HTTP cache, force a fresh bundle download
        const url = new URL(window.location.origin);
        url.searchParams.set('_cb', String(Date.now()));
        window.location.replace(url.toString());
      }, 2000);
    } catch (e) {
      push(`Error: ${e.message}`);
    } finally {
      setRunning(false);
    }
  };

  // Auto-run on page open — one click to arrive = one action done
  useEffect(() => {
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: '#000',
        color: '#fff',
        fontFamily: 'system-ui, sans-serif',
      }}
      data-testid="clear-cache-page"
    >
      <div
        style={{
          width: '100%',
          maxWidth: 560,
          background: '#111',
          border: '2px solid #f4b400',
          borderRadius: 16,
          padding: 32,
          boxShadow: '0 20px 60px rgba(244,180,0,0.2)',
        }}
      >
        <div style={{ fontSize: 28, fontWeight: 900, color: '#f4b400', marginBottom: 8 }}>
          Refresh the Casa Samba app
        </div>
        <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', marginBottom: 24, lineHeight: 1.5 }}>
          This page clears your browser's cached copy of the site and loads the newest version.
          Safe to run anytime. Your login stays intact.
        </div>

        <div
          style={{
            background: '#000',
            border: '1px solid rgba(244,180,0,0.3)',
            borderRadius: 10,
            padding: 16,
            minHeight: 180,
            maxHeight: 320,
            overflowY: 'auto',
            fontFamily: 'ui-monospace, Menlo, monospace',
            fontSize: 13,
            lineHeight: 1.55,
          }}
          data-testid="clear-cache-log"
        >
          {log.length === 0 && !running && <span style={{ opacity: 0.5 }}>Preparing...</span>}
          {log.map((l, i) => (
            <div key={i} style={{ color: '#9ca3af' }}>
              <span style={{ color: '#f4b400' }}>• </span>{l}
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button
            onClick={run}
            disabled={running}
            data-testid="clear-cache-run-btn"
            style={{
              flex: 1,
              padding: '12px 20px',
              borderRadius: 999,
              border: 'none',
              background: running ? '#555' : '#f4b400',
              color: '#000',
              fontWeight: 900,
              fontSize: 15,
              cursor: running ? 'not-allowed' : 'pointer',
            }}
          >
            {done ? 'Clear again' : running ? 'Working...' : 'Clear cache now'}
          </button>
          <a
            href="/"
            style={{
              padding: '12px 20px',
              borderRadius: 999,
              border: '2px solid rgba(244,180,0,0.5)',
              color: '#f4b400',
              fontWeight: 900,
              fontSize: 15,
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
            }}
            data-testid="clear-cache-home-link"
          >
            Back to site
          </a>
        </div>

        <div style={{ marginTop: 20, fontSize: 12, color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 }}>
          Tip: bookmark this page as "Refresh Casa Samba" so you can come back to it any time
          — one click will always load the latest version of the site.
        </div>
      </div>
    </div>
  );
};

export default ClearCache;
